import { useState } from 'react';

import TagManager from 'react-gtm-module';

import { Language } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$types';
import { useDidMount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { AppHelper, LoggerHelper } from '@helpers';
import { useAlert, useCookiesConsent, useModal, useTranslation } from '@hooks';
import { useFetchAccountDeleteCookies } from '@services';
import {
	useAmaStore,
	useBaseStore,
	useCookiesStore,
	useLoginStore,
	useSearchParamsStore,
	useServicesStore,
} from '@store';

export const useLoginScreenHelper = () => {
	const { fetchContents } = useTranslation();
	const genericErrorModal = useModal('genericError');
	const sessionExpiredModal = useModal('sessionExpired');
	const invalidDataAlert = useAlert('invalidData');
	const invalidCaptchaAlert = useAlert('invalidCaptcha');

	const { initCookiesConsentModal } = useCookiesConsent();

	const language = useSearchParamsStore((state) => state.language);
	const cookieResponseType = useCookiesStore((state) => state.responseType);
	const alias = useLoginStore((state) => state.form?.alias);
	const accountChallengeErrorCode = useServicesStore((state) => state.accountChallenge?.errorCode);
	const screenGoto = useBaseStore((state) => state.goto);
	const resetLoginFormState = useLoginStore((state) => state.resetFormState);
	const isAmaRedirectStore = useAmaStore((state) => state.isAmaRedirect);

	const { fetch: fetchAccountDeleteCookies } = useFetchAccountDeleteCookies();

	const [isLoading, setIsLoading] = useState(true);

	useDidMount(() => {
		handleInit();
	});

	const handleInit = async () => {
		try {
			// get both generic and client translations
			const translations = await fetchContents(language || Language.pt);

			let showCookiesConsent = translations.showCookiesConsentA;
			let gtmActive = translations['configs.gtm.activeA'];

			if (window.location.pathname.includes('/b/')) {
				showCookiesConsent = translations.showCookiesConsentB;
				gtmActive = translations['configs.gtm.activeB'];
			}

			// manage GTM script injection depending on configuration
			const gtmContainerId = translations['configs.gtm.containerId'];
			if (!!gtmContainerId && gtmActive === 'true') {
				TagManager.initialize({
					gtmId: gtmContainerId,
				});
			}

			// manage cookies modal visibility and cookie response from BE
			await Promise.all([initCookiesConsentModal(showCookiesConsent), handleCookieResponse()]);
		} catch {
			screenGoto('error');
		} finally {
			setIsLoading(false);
		}
	};

	const handleCookieResponse = async () => {
		if (cookieResponseType) {
			/* clear the cookies stored in browser via service */
			await fetchAccountDeleteCookies({});

			if (cookieResponseType === 'invalidCredentials' && isAmaRedirectStore) {
				genericErrorModal.show();
				return;
			}

			/* Process based on the cookie response type */
			switch (cookieResponseType) {
				case 'serviceUnavailable':
					/* Show a generic error modal for service unavailable */
					genericErrorModal.show();
					break;
				case 'invalidCredentials':
				case 'invalidCaptcha':
					/* Clear all form values except for alias */
					resetLoginFormState({ alias });

					if (AppHelper.isSessionExpired(accountChallengeErrorCode)) {
						/* Show session expired modal */
						sessionExpiredModal.show();
					} else {
						if (cookieResponseType === 'invalidCaptcha') {
							/* Show the invalid captcha alert */
							invalidCaptchaAlert.show();
						} else {
							/* Show an invalid data alert for other cases of invalid credentials */
							invalidDataAlert.show();
						}
					}
					break;
				default:
					/* Handle unexpected cookie response types */
					LoggerHelper.log(
						'App',
						'Login > handleCookieResponse > cookieResponseType not handled:',
						cookieResponseType,
						{ overlay: { show: true } }
					);

					return Promise.reject();
			}
		}
	};

	return {
		isLoading,
	};
};
